import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { VERT, FRAG } from "./coffeeShader";

// Full-screen coffee portal: real brewed coffee rises toward the camera,
// fills the frame, holds inside the surface, then opens onto the next page.
const CoffeePortal = forwardRef(function CoffeePortal(_, ref) {
  const canvasRef = useRef(null);
  const glRef = useRef(null);
  const [active, setActive] = useState(false);

  const ensure = useCallback(async () => {
    if (glRef.current) return glRef.current;
    const THREE = await import("three");
    const canvas = canvasRef.current;
    const isMobile = window.innerWidth < 768;
    const renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: false,
      powerPreference: "high-performance",
    });
    const dpr = Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2);
    renderer.setPixelRatio(dpr);
    renderer.setSize(window.innerWidth, window.innerHeight, false);
    const scene = new THREE.Scene();
    const camera = new THREE.Camera();
    const uniforms = {
      u_res: { value: new THREE.Vector2(window.innerWidth * dpr, window.innerHeight * dpr) },
      u_time: { value: 0 },
      u_progress: { value: 0 },
      u_dive: { value: 0 },
      u_oct: { value: isMobile ? 3 : 5 },
    };
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(2, 2),
      new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms, transparent: true })
    );
    scene.add(mesh);
    glRef.current = { renderer, uniforms, scene, camera };
    return glRef.current;
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      async play(onMid, onDone) {
        setActive(true);
        let gl;
        try {
          gl = await ensure();
        } catch {
          onMid();
          onDone && onDone();
          setActive(false);
          return;
        }
        const { renderer, uniforms, scene, camera } = gl;
        const canvas = canvasRef.current;
        canvas.style.opacity = "1";
        uniforms.u_progress.value = 0;
        uniforms.u_dive.value = 0;

        const t0 = performance.now();
        const FILL = 1250;
        const HOLD = 420;
        const REVEAL = 950;
        const easeIn = (x) => x * x * x;
        const easeOut = (x) => 1 - Math.pow(1 - x, 3);
        let midCalled = false;

        function frame(now) {
          const el = now - t0;
          uniforms.u_time.value = el / 1000;
          if (el < FILL) {
            uniforms.u_progress.value = easeIn(el / FILL);
          } else if (el < FILL + HOLD) {
            uniforms.u_progress.value = 1;
            uniforms.u_dive.value = easeOut((el - FILL) / HOLD) * 0.5;
          } else {
            if (!midCalled) {
              midCalled = true;
              onMid();
            }
            const r = Math.min(1, (el - FILL - HOLD) / REVEAL);
            uniforms.u_dive.value = 0.5 + r * 0.5;
            canvas.style.opacity = String(1 - r);
          }
          renderer.render(scene, camera);
          if (el < FILL + HOLD + REVEAL) {
            requestAnimationFrame(frame);
          } else {
            uniforms.u_progress.value = 0;
            uniforms.u_dive.value = 0;
            setActive(false);
            onDone && onDone();
          }
        }
        requestAnimationFrame(frame);
      },
    }),
    [ensure]
  );

  return (
    <div
      className={`fixed inset-0 z-[95] ${active ? "pointer-events-auto" : "pointer-events-none"}`}
      style={{ visibility: active ? "visible" : "hidden" }}
      aria-hidden="true"
      data-testid="coffee-portal"
    >
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>
  );
});

export default CoffeePortal;
